import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    ReactNode,
} from "react"
import { getCreditBalance, getCreditPricing } from "../../services/credit-service"

export interface DurationCost {
    days: number
    cost: number
}

export interface CreditPricing {
    freeJobPosts: number
    freeJobDurationDays: number
    jobPost: DurationCost[]
    promoteGig: DurationCost[]
    promoteEmployer: DurationCost[]
    promoteEvent: DurationCost[]
    eventCreate: number
    republishEvent: number
    invitationCost: number
    notifyMinCost: number
    notifyCandidatesPerCredit: number
    signupBonus: number
    lowBalanceThreshold: number
    creditExpiryMonths: number
    packages: { packageName: string; label: string; credits: number; priceInBani: number; originalPriceInBani: number }[]
}

interface CreditsContextValue {
    /** Soldul curent de credite, sau null dacă nu a fost încărcat încă. */
    balance: number | null
    /** Data ISO la care expiră următorul lot de credite (sau null). */
    nextExpiringAt: string | null
    /** Câte credite expiră la nextExpiringAt. */
    nextExpiringAmount: number
    /** Catalogul de tarife/parametri (încărcat o singură dată). */
    pricing: CreditPricing | null
    /** True când soldul e sub pragul de avertizare. */
    lowBalance: boolean
    /** Reîncarcă soldul din backend. Întoarce noua valoare (sau null la eroare). */
    refresh: () => Promise<number | null>
    /**
     * După o plată, creditarea contului se face prin webhook Stripe (asincron), deci soldul
     * nu se schimbă instant. Reîncearcă câteva secunde până se reflectă noul sold.
     */
    pollForChange: (previous: number | null, attempts?: number) => void
}

const CreditsContext = createContext<CreditsContextValue>({
    balance: null,
    nextExpiringAt: null,
    nextExpiringAmount: 0,
    pricing: null,
    lowBalance: false,
    refresh: async () => null,
    pollForChange: () => {},
})

export const useCredits = () => useContext(CreditsContext)

export const CreditsProvider = ({ children }: { children: ReactNode }) => {
    const [balance, setBalance] = useState<number | null>(null)
    const [nextExpiringAt, setNextExpiringAt] = useState<string | null>(null)
    const [nextExpiringAmount, setNextExpiringAmount] = useState(0)
    const [pricing, setPricing] = useState<CreditPricing | null>(null)
    const pollTimer = useRef<ReturnType<typeof setTimeout>>()

    const refresh = useCallback(async (): Promise<number | null> => {
        if (!localStorage.getItem("jwtToken")) return null
        try {
            const r: any = await getCreditBalance()
            const b = typeof r?.balance === "number" ? r.balance : null
            setBalance(b)
            setNextExpiringAt(r?.nextExpiringAt ?? null)
            setNextExpiringAmount(typeof r?.nextExpiringAmount === "number" ? r.nextExpiringAmount : 0)
            return b
        } catch {
            return null
        }
    }, [])

    const pollForChange = useCallback(
        (previous: number | null, attempts = 6) => {
            if (pollTimer.current) clearTimeout(pollTimer.current)
            // Reținem soldul de referință (dinainte de creditare). Dacă nu îl știm încă
            // (context neîncărcat), îl fixăm la primul răspuns, ca să nu oprim prematur.
            let baseline = previous
            let left = attempts
            const tick = async () => {
                const b = await refresh()
                left -= 1
                if (baseline == null) baseline = b
                const credited = b != null && baseline != null && b !== baseline
                if (left > 0 && !credited) {
                    pollTimer.current = setTimeout(tick, 2000)
                }
            }
            pollTimer.current = setTimeout(tick, 1500)
        },
        [refresh]
    )

    useEffect(() => {
        refresh()
        if (localStorage.getItem("jwtToken")) {
            getCreditPricing().then((p: any) => setPricing(p)).catch(() => {})
        }
        return () => {
            if (pollTimer.current) clearTimeout(pollTimer.current)
        }
    }, [refresh])

    const lowBalance =
        balance != null && pricing != null && balance < pricing.lowBalanceThreshold

    return (
        <CreditsContext.Provider
            value={{
                balance,
                nextExpiringAt,
                nextExpiringAmount,
                pricing,
                lowBalance,
                refresh,
                pollForChange,
            }}
        >
            {children}
        </CreditsContext.Provider>
    )
}
